import {
  createAsyncThunk,
  createEntityAdapter,
  createSelector,
  createSlice,
  isAnyOf,
} from '@reduxjs/toolkit';
// @ts-ignore
import mime from 'mime-types';

import { del, get, patch, post, put } from 'data/services/config';
import { Artifact } from './artifactsTypes';
import { categoriesToDynamicFields } from './eventsHelpers';
import { Event, EventUser } from './eventsTypes';

// Adapter for operate and normalize the data
export const eventsAdapter = createEntityAdapter<Event>();

const initialState = eventsAdapter.getInitialState({
  loading: false,
  saving: false,
  loadingUsers: false,
  organizersByEventId: {} as Record<string, EventUser[]>,
  navigatorsByEventId: {} as Record<string, EventUser[]>,
});

// Actions
export const fetchEvent = createAsyncThunk(
  'events/fetch',
  async (eventId: string, { rejectWithValue }) => {
    try {
      return await get(`/events/${eventId}`);
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const updateEvent = createAsyncThunk(
  'events/update',
  async (event: Event, { rejectWithValue }) => {
    try {
      const { id, ...rest } = event;
      return await put(`/events/${id}`, { event: rest });
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const addUsers = createAsyncThunk(
  'events/addUsers',
  async (
    { eventId, userType, emails }: { eventId: string; userType: string; emails: string[] },
    { rejectWithValue },
  ) => {
    try {
      return await put(`/events/${eventId}/users`, { user_type: userType, emails });
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const fetchEventUsers = createAsyncThunk(
  'events/fetchEventUsers',
  async (eventId: string, { rejectWithValue }) => {
    try {
      const result = await get(`/events/${eventId}/users`);
      return { eventId, ...result };
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const removeUser = createAsyncThunk(
  'events/removeUser',
  async ({ eventId, userId }: { eventId: string; userId: string }, { rejectWithValue }) => {
    try {
      const result = await del(`/events/${eventId}/users`, { user_id: userId });
      return { eventId, ...result };
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const saveArtifactsOrder = createAsyncThunk(
  'events/saveArtifactsOrder',
  async (
    { eventId, artifactIds }: { eventId: string; artifactIds: number[] },
    { rejectWithValue },
  ) => {
    try {
      const result = await patch(`/events/${eventId}/artifacts/save_order`, {
        artifact_ids: artifactIds,
      });
      return { eventId, ...result };
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const addArtifact = createAsyncThunk(
  'events/addArtifact',
  async ({ eventId, artifact }: { eventId: string; artifact: Artifact }, { rejectWithValue }) => {
    try {
      const result = await post(`/events/${eventId}/artifacts`, artifact);
      return { eventId, ...result };
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const updateArtifact = createAsyncThunk(
  'events/updateArtifact',
  async (
    { eventId, artifactId, artifact }: { eventId: string; artifactId: number; artifact: Artifact },
    { rejectWithValue },
  ) => {
    try {
      const result = await put(`/events/${eventId}/artifacts/${artifactId}`, artifact);
      return { eventId, ...result };
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const submitForReview = createAsyncThunk(
  'events/submitForReview',
  async (
    {
      eventId,
      organizerId,
      subject,
      content,
    }: {
      eventId: string;
      organizerId: string;
      subject: string;
      content: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const result = await post(`/events/${eventId}/submit_for_review`, {
        organizer_id: organizerId,
        subject,
        content,
      });
      return { eventId, ...result };
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const eventsSlice = createSlice({
  name: 'eventsSlice',
  initialState,
  reducers: {
    reorderArtifacts: (state, action) => {
      const { eventId, fromIndex, toIndex } = action.payload;
      const event = state.entities[eventId];
      if (event?.artifacts) {
        const artifacts = [...event.artifacts];
        const [movedItem] = artifacts.splice(fromIndex, 1);
        artifacts.splice(toIndex, 0, movedItem);
        event.artifacts = artifacts;
        action.payload.artifacts = artifacts;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchEvent.fulfilled, (state, action) => {
        eventsAdapter.upsertOne(state, action.payload.event);
        state.loading = false;
      })
      .addCase(updateEvent.fulfilled, (state, action) => {
        eventsAdapter.upsertOne(state, action.payload.event);
        state.saving = false;
      })
      .addCase(addUsers.fulfilled, (state, action) => {
        const eventId = action.meta.arg.eventId;
        state.organizersByEventId[eventId] = action.payload.organizers;
        state.navigatorsByEventId[eventId] = action.payload.navigators;
        state.loadingUsers = false;
        state.saving = false;
      })
      .addCase(fetchEventUsers.fulfilled, (state, action) => {
        const eventId = action.meta.arg;
        const { organizers, navigators } = action.payload;
        state.organizersByEventId[eventId] = organizers;
        state.navigatorsByEventId[eventId] = navigators;
        state.loadingUsers = false;
      })
      .addCase(removeUser.fulfilled, (state, action) => {
        const eventId = action.meta.arg.eventId;
        const { organizers, navigators } = action.payload;
        state.organizersByEventId[eventId] = organizers;
        state.navigatorsByEventId[eventId] = navigators;
        state.loadingUsers = false;
      })
      .addCase(saveArtifactsOrder.fulfilled, (state, action) => {
        const eventId = action.meta.arg.eventId;
        const event = state.entities[eventId];
        if (event && action.payload.artifacts) {
          event.artifacts = action.payload.artifacts;
        }
        state.saving = false;
      })
      .addCase(addArtifact.fulfilled, (state, action) => {
        const eventId = action.meta.arg.eventId;
        if (state.entities[eventId] && action.payload.artifacts) {
          state.entities[eventId].artifacts = action.payload.artifacts;
        }
        state.saving = false;
      })
      .addCase(updateArtifact.fulfilled, (state, action) => {
        const eventId = action.meta.arg.eventId;
        if (state.entities[eventId] && action.payload.artifacts) {
          state.entities[eventId].artifacts = action.payload.artifacts;
        }
        state.saving = false;
      })
      .addMatcher(isAnyOf(fetchEvent.pending), (state) => {
        state.loading = true;
      })
      .addMatcher(isAnyOf(fetchEvent.fulfilled, fetchEvent.rejected), (state) => {
        state.loading = false;
      })
      .addMatcher(
        isAnyOf(
          updateEvent.pending,
          saveArtifactsOrder.pending,
          addArtifact.pending,
          updateArtifact.pending,
          submitForReview.pending,
        ),
        (state) => {
          state.saving = true;
        },
      )
      .addMatcher(
        isAnyOf(
          updateEvent.fulfilled,
          updateEvent.rejected,
          saveArtifactsOrder.rejected,
          addArtifact.rejected,
          updateArtifact.rejected,
          submitForReview.fulfilled,
          submitForReview.rejected,
        ),
        (state) => {
          state.saving = false;
        },
      )
      .addMatcher(isAnyOf(fetchEventUsers.pending, removeUser.pending), (state) => {
        state.loadingUsers = true;
      })
      .addMatcher(
        isAnyOf(
          fetchEventUsers.fulfilled,
          fetchEventUsers.rejected,
          removeUser.fulfilled,
          removeUser.rejected,
        ),
        (state) => {
          state.loadingUsers = false;
        },
      )
      .addMatcher(isAnyOf(addUsers.pending), (state) => {
        state.loadingUsers = true;
        state.saving = true;
      })
      .addMatcher(isAnyOf(addUsers.fulfilled, addUsers.rejected), (state) => {
        state.loadingUsers = false;
        state.saving = false;
      });
  },
  selectors: {
    ...eventsAdapter.getSelectors(),
    getIsLoading: (state) => state.loading,
    getIsSaving: (state) => state.saving,
    getIsLoadingUsers: (state) => state.loadingUsers,
    getOrganizersByEventId: createSelector(
      (state) => state.organizersByEventId,
      (_, eventId: string) => eventId,
      (organizersByEventId, eventId) => organizersByEventId[eventId] || [],
    ),
    getNavigatorsByEventId: createSelector(
      (state) => state.navigatorsByEventId,
      (_, eventId: string) => eventId,
      (navigatorsByEventId, eventId) => navigatorsByEventId[eventId] || [],
    ),
    getEventBySlug: createSelector(
      eventsAdapter.getSelectors().selectAll,
      (_, slug: string) => slug,
      (events: Event[], slug: string) => {
        const event = events.find((event) => event.slug === slug);
        if (!event) return undefined;

        const artifacts = (event.artifacts || []).map((artifact) => {
          return {
            ...artifact,
            file_type: mime.types[artifact.file_type],
          };
        });

        return {
          ...event,
          categories: categoriesToDynamicFields(event.matching_categories),
          artifacts,
        };
      },
    ),
  },
});

export default eventsSlice.reducer;
export const fromEvents = eventsSlice.selectors;
